import { useState, useRef, useCallback, useEffect } from 'react';
import { TOOLTIP_DELAY } from '../constants/layout';
import { useZoom } from '../contexts/ZoomContext';

/**
 * Custom hook for tooltip functionality with delayed show and position tracking.
 * Automatically hides tooltip when disabled (e.g., during drag operations).
 * Handles coordinate adjustment for Safari with CSS zoom.
 * 
 * @param {Object} config
 * @param {string} config.title - Tooltip title text
 * @param {string} [config.description] - Optional tooltip description text
 * @param {number} [config.delay=TOOLTIP_DELAY] - Delay in ms before showing tooltip
 * @param {boolean} [config.disabled=false] - Whether tooltip should be hidden
 * @param {string} [config.placement='top'] - Tooltip placement: 'top' or 'bottom'
 * @returns {Object} Tooltip state and handlers
 * @returns {Object} returns.tooltipProps - Props to pass to Tooltip component
 * @returns {Function} returns.handleMouseEnter - Call on element mouseenter
 * @returns {Function} returns.handleMouseLeave - Call on element mouseleave
 */
function useTooltip({ 
  title, 
  description, 
  delay = TOOLTIP_DELAY, 
  disabled = false,
  placement = 'top'
}) {
  const { scaleFactor, isSafari } = useZoom();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const tooltipTimeoutRef = useRef(null);

  // Clear tooltip timeout helper
  const clearTooltipTimeout = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback((e) => {
    if (disabled) return;
    clearTooltipTimeout();
    // Calculate position based on element bounds
    const rect = e.currentTarget.getBoundingClientRect();
    
    // Safari with CSS zoom returns unzoomed coordinates, need to scale them
    // Chrome returns already-zoomed coordinates, use them as-is
    const adjustedRect = isSafari ? {
      left: rect.left * scaleFactor,
      right: rect.right * scaleFactor,
      top: rect.top * scaleFactor,
      bottom: rect.bottom * scaleFactor,
      width: rect.width * scaleFactor
    } : rect;
    
    const y = placement === 'bottom' 
      ? adjustedRect.bottom + 8  // Position below the element
      : adjustedRect.top - 8;     // Position above the element
    setTooltipPos({ 
      x: adjustedRect.left + adjustedRect.width / 2, 
      y 
    });
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, delay);
  }, [disabled, delay, placement, scaleFactor, isSafari, clearTooltipTimeout]);

  const handleMouseLeave = useCallback(() => {
    clearTooltipTimeout();
    setShowTooltip(false);
  }, [clearTooltipTimeout]);

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Tooltip is visible only when showTooltip is true and not disabled
  const visible = showTooltip && !disabled;

  return {
    tooltipProps: {
      visible,
      x: tooltipPos.x,
      y: tooltipPos.y,
      title,
      description,
      placement
    },
    handleMouseEnter,
    handleMouseLeave
  };
}

export default useTooltip;
